import { useNavigate } from "react-router-dom";
import Button from "../common/Button";
import OTPInput from "./OTPInput";
import { getStoredItem, setStoredItem } from "../../config/storageUtils";
import { useState } from "react";
import api from "../../api/ApiService";
import { Spinner } from "../../icons/CommonIcons";
import { INVEST_UK_WEBSITE_URL } from "../../config/apiConfig";
import { useLanguage } from "../LanguageProvider/LanguageProvider";
import { useAuth } from "./AuthProvider";

const OTPForm = ({ cafId }) => {
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [otpError, setOtpError] = useState(null);
  const navigate = useNavigate();
  const [requesting, setRequesting] = useState(false);
  const [requestViaCall, setRequestViaCall] = useState(false);
  const { language, setLanguage } = useLanguage();
  const { setTokenGenerated, setUserType } = useAuth();

  const handleSubmit = async () => {
    const otpValue = otp.join("");
    if (!/^\d{6}$/.test(otpValue)) {
      setOtpError({
        en: "OTP must be 6 digits",
        hi: "ओटीपी 6 अंकों का होना चाहिए",
      });
      return;
    }

    setOtpError(null);

    try {
      setRequesting(true);

      await api.initCsrf();

      await api.post("/verify-otp", {
        otp: otpValue,
        caf_id: cafId,
      });

      let authResp;
      try {
        authResp = await api.get("/auth/check");
      } catch {
        setOtpError({
          en: "OTP verification failed",
          hi: "ओटीपी सत्यापन विफल",
        });
        return;
      }

      if (!authResp?.status) {
        setOtpError({
          en: "Invalid or expired OTP",
          hi: "अमान्य या समाप्त ओटीपी",
        });
        return;
      }

      setUserType(authResp.userType || "user");

      const manualInserted = await api.get(`/manual-inserted`);
      if (manualInserted.status !== 200) {
        setOtpError({
          en: "Failed to fetch automatic data",
          hi: "स्वचालित डेटा लाने में विफल",
        });
        setOtp(Array(6).fill(""));
        return;
      }

      if (manualInserted.manual_data_inserted) {
        const manualDataResponse = await api.get(`/manual-data`);
        if (!manualDataResponse) {
          setOtpError({
            en: "Failed to fetch manual data",
            hi: "मैनुअल डेटा लाने में विफल",
          });
          setOtp(Array(6).fill(""));
          return;
        }

        const manualData = JSON.parse(manualDataResponse.details);

        if (
          manualData.available_incentive_scheme ===
            "uttarakhandtourismpolicy2023" ||
          manualData.construction_status?.toLowerCase() ===
            "under construction" ||
          manualData.construction_status?.toLowerCase() ===
            "construction not started"
        ) {
          setTokenGenerated(false);
          navigate("/automatic-data");
          setOtp(Array(6).fill(""));
          return;
        }

        setTokenGenerated(true);
        navigate("/dashboard");
        setOtp(Array(6).fill(""));
        return;
      }
      setTokenGenerated(false);
      navigate("/automatic-data");

      setOtp(Array(6).fill(""));
    } catch (error) {
      console.error(
        "OTP verify error:",
        error?.response?.data || error.message,
      );

      setOtpError({
        en: "Invalid or expired OTP",
        hi: "अमान्य या समाप्त ओटीपी",
      });
    } finally {
      setRequesting(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const updatedOtp = [...otp];
    updatedOtp[index] = value;
    setOtp(updatedOtp);

    if (updatedOtp.every((digit) => digit !== "")) {
      setOtpError("");
    } else {
      setOtpError({
        en: "OTP must be 6 digits",
        hi: "ओटीपी 6 अंकों का होना चाहिए",
      });
    }
  };

  const handleRequestAgain = async (type) => {
    try {
      if (type === "call") {
        setRequestViaCall(true);
      } else {
        setRequesting(true);
      }
      setOtp(Array(6).fill(""));
      setOtpError(null);
      await api.initCsrf();
      const res = await api.post("/request-otp", {
        caf_id: cafId,
      });
      if (res.status !== 200) {
        setOtpError({
          en: "Failed to request OTP",
          hi: "ओटीपी का अनुरोध करने में विफल",
        });
        return;
      }
      setOtpError({
        en: "Code sent to your email or registered mobile number.",
        hi: "कोड आपके ईमेल या पंजीकृत मोबाइल नंबर पर भेजा गया है।",
      });
    } catch (error) {
      setOtpError({
        en: "An error occurred while requesting OTP",
        hi: "ओटीपी का अनुरोध करते समय एक गलती हुई",
      });
    } finally {
      setRequestViaCall(false);
      setRequesting(false);
    }
  };

  return (
    <>
      <p className="capitalize text-center">
        {language
          ? "कोड आपके ईमेल या पंजीकृत मोबाइल नंबर पर भेजा गया है।"
          : "Code sent to your email or registered mobile number."}
      </p>
      <div>
        <OTPInput value={otp} onChange={handleOtpChange} />
        {otpError && (
          <p className="text-red-500 text-sm mt-2 text-center">
            {language ? otpError.hi : otpError.en}
          </p>
        )}
        <p className="text-center text-gray-500 mt-4">
          {language ? "कोड प्राप्त नहीं हुआ?" : "Didn't receive the code?"}
        </p>

        <div className="flex justify-between mt-4">
          {requestViaCall ? (
            <Button className="text-theme-color flex items-center justify-center gap-2">
              {language ? "निवेदन है..." : "Requesting..."}
              <Spinner />
            </Button>
          ) : (
            <Button
              className="text-theme-color"
              onClick={() => handleRequestAgain("call")}
            >
              {language ? "कॉल के माध्यम से अनुरोध करें" : "Request Via Call"}
            </Button>
          )}
          {requesting ? (
            <Button className="text-theme-color flex items-center justify-center gap-2">
              {language ? "निवेदन है..." : "Requesting..."}
              <Spinner />
            </Button>
          ) : (
            <Button
              className="text-theme-color"
              onClick={() => handleRequestAgain("again")}
            >
              {language ? "पुनः अनुरोध करें" : "Request Again"}
            </Button>
          )}
        </div>

        <Button
          className="w-full bg-btn font-semibold py-2 rounded-md mt-6"
          onClick={handleSubmit}
        >
          {language ? "जाँचें" : "Verify"}
        </Button>
      </div>
    </>
  );
};

export default OTPForm;
